import type { PropsWithChildren, ReactNode } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { tv } from "tailwind-variants";

const statTileVariants = tv({
  slots: {
    root: "relative rounded-lg border border-line px-4 py-3",
    label: "text-xs font-semibold tracking-wider text-content-muted uppercase",
    toggle: [
      "cursor-pointer text-left uppercase",
      "after:absolute after:inset-0 after:rounded-lg",
      "focus:outline-none focus-visible:after:ring-2 focus-visible:after:ring-focus-ring",
    ],
    value: "mt-1 text-2xl font-semibold text-content-strong tabular-nums",
    detail: "block text-xs font-normal text-content-muted",
    hint: "mt-1 flex items-center gap-1 text-xs font-medium text-accent-text",
    chevron: "size-4 shrink-0 transition-transform duration-300",
  },
  variants: {
    isWarning: {
      true: { value: "text-danger-content" },
    },
    isExpandable: {
      true: { root: "transition-colors duration-150 hover:bg-surface-hover" },
    },
    isExpanded: {
      true: { root: "border-accent ring-1 ring-accent", chevron: "rotate-180" },
    },
  },
});

type StatTileProps = {
  label: ReactNode;
  detail?: ReactNode;
  isWarning?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
  toggleHint?: string;
};

export const StatTile = ({
  children,
  detail,
  isExpanded = false,
  isWarning = false,
  label,
  onToggle,
  toggleHint,
}: PropsWithChildren<StatTileProps>) => {
  const {
    chevron,
    detail: detailClass,
    hint,
    label: labelClass,
    root,
    toggle,
    value,
  } = statTileVariants({ isExpandable: Boolean(onToggle), isExpanded, isWarning });

  return (
    <div className={root()}>
      <dt className={labelClass()}>
        {onToggle ? (
          <button aria-expanded={isExpanded} className={toggle()} onClick={onToggle} type="button">
            {label}
          </button>
        ) : (
          label
        )}
      </dt>

      <dd className={value()}>
        {children}

        {detail ? <small className={detailClass()}>{detail}</small> : null}

        {onToggle ? (
          <span className={hint()}>
            {toggleHint}

            <ChevronDownIcon aria-hidden="true" className={chevron()} />
          </span>
        ) : null}
      </dd>
    </div>
  );
};
